import type { RespuestaPublicar, ResultadoPublicacion } from './consultas'
import { formatearFecha } from './formato'
import { InsigniaEstadoPublicacion } from './insignias'
import { IconoAviso, IconoCorrecto } from '../../componentes/iconos'
import { BarraPaginacion } from '../../componentes/BarraPaginacion'
import { usePaginacionLocal } from '../../componentes/paginacion'

/**
 * Que ha pasado con cada cliente. Uno por cliente y nunca un error global: en
 * una lista de veinte, "ha fallado" a secas no dice cuales se han creado, y
 * volver a publicar por si acaso es exactamente lo que crea el albaran de mas.
 *
 * - `publicado` — albarán creado. No hay evento de calendario (spec 002).
 * - `duplicado` — ya habia un albarán de ese cliente ese dia y no se ha creado
 *   otro. Es la idempotencia haciendo su trabajo, no un error.
 * - `ambiguo` / `fallido` / `error` — no hay albarán. Estos si hay que arreglarlos.
 */

/** Los que no tienen albarán y tiene sentido volver a intentar. */
export const REINTENTABLES = new Set(['fallido', 'error'])

interface Props {
  respuesta: RespuestaPublicar
  reintentando: boolean
  onReintentar: () => void
}

function Fila({ resultado }: { resultado: ResultadoPublicacion }) {
  return (
    <li className="flex flex-col gap-1 border-b border-borde-suave px-4 py-3 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium text-tinta">{resultado.nombre}</span>
        <InsigniaEstadoPublicacion estado={resultado.estado} />
        {resultado.presupuesto ? (
          <span className="text-sm text-tinta-2">Albarán {resultado.presupuesto}</span>
        ) : null}
      </div>
      {resultado.mensaje ? (
        <p className="text-sm text-tinta-apagada">{resultado.mensaje}</p>
      ) : null}
    </li>
  )
}

export function PanelResultados({ respuesta, reintentando, onReintentar }: Props) {
  const resultados = respuesta.resultados
  const pag = usePaginacionLocal(resultados)
  const cuenta = (...estados: string[]) => resultados.filter((r) => estados.includes(r.estado)).length
  const pendientes = resultados.filter((r) => REINTENTABLES.has(r.estado))
  const creados = cuenta('publicado', 'publicado_sin_evento')
  const sinPublicar = cuenta('fallido', 'ambiguo', 'error')
  // Todo bien si nada se quedo sin publicar (un duplicado no es un fallo: el
  // albaran ya existia y no se ha creado otro).
  const todoOk = resultados.length > 0 && sinPublicar === 0

  return (
    <section
      aria-label="Resultado de la publicación"
      className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie"
    >
      {/* Encabezado del desenlace: que no quede duda de si se ha publicado. */}
      <div className="border-b border-borde px-4 pb-3 pt-4">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className={`grid size-7 shrink-0 place-items-center rounded-full ${
              todoOk ? 'bg-positivo-fondo text-positivo-tinta' : 'bg-atencion-fondo text-atencion-tinta'
            }`}
          >
            {todoOk ? <IconoCorrecto className="size-4" /> : <IconoAviso className="size-4" />}
          </span>
          <h2 className="text-sm font-semibold text-tinta">
            {!todoOk
              ? 'Publicación con incidencias'
              : creados === 0
                ? 'Los albaranes ya existían'
                : creados === 1
                  ? 'Albarán creado'
                  : `${creados} albaranes creados`}
          </h2>
        </div>
        <p className="mt-1.5 text-sm text-tinta-apagada">
          Publicación del {formatearFecha(respuesta.fecha_reparto)} · {creados} publicados,{' '}
          {cuenta('duplicado')} ya existían, {sinPublicar} sin publicar.
        </p>
        {respuesta.aviso ? (
          <p className="mt-2 rounded-lg border border-atencion-anillo bg-atencion-fondo px-3 py-2 text-sm text-atencion-tinta">
            {respuesta.aviso}
          </p>
        ) : null}
      </div>

      <ul aria-label="Resultado por cliente" className="flex flex-col">
        {pag.visibles.map((resultado) => (
          <Fila key={`${resultado.contact_id}-${resultado.presupuesto}`} resultado={resultado} />
        ))}
      </ul>
      {pag.paginas > 1 ? (
        <div className="border-t border-borde px-4 py-3">
          <BarraPaginacion pagina={pag.pagina} paginas={pag.paginas} onCambiar={pag.irAPagina} etiqueta="Paginación de los resultados" />
        </div>
      ) : null}

      {pendientes.length > 0 ? (
        <div className="flex flex-col gap-2 border-t border-borde px-4 py-3">
          <p className="text-sm text-tinta-apagada">
            Los albaranes que ya existen no se vuelven a crear: reintentar es seguro.
          </p>
          <button
            type="button"
            disabled={reintentando}
            onClick={onReintentar}
            className="self-start rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm font-medium text-tinta-2 transition-colors hover:bg-elevado disabled:cursor-not-allowed disabled:text-desactivado-tinta"
          >
            {reintentando
              ? 'Reintentando'
              : `Reintentar ${pendientes.length === 1 ? 'el cliente' : `los ${pendientes.length} clientes`} que no han terminado`}
          </button>
        </div>
      ) : null}
    </section>
  )
}
